import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Phone, Globe, Bookmark, BookmarkCheck, Building2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

export default function BusinessDetail() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  // Review form
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  async function loadBusiness() {
    try {
      const data = await api(`/businesses/${businessId}`);
      setBusiness(data.business);
      const savedRes = await api('/businesses/saved/me').catch(() => ({ businesses: [] }));
      setSaved((savedRes.businesses || []).some((b) => b.id === businessId));
    } catch (err) {
      toast.error(err.message || 'Failed to load');
      navigate('/app/businesses');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  async function handleToggleSave() {
    try {
      const res = await api(`/businesses/${businessId}/save`, { method: 'POST' });
      setSaved(res.saved);
      toast.success(res.saved ? 'Saved' : 'Removed');
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function handleSubmitReview(e) {
    e.preventDefault();
    if (!comment.trim()) return toast.error('Please add a comment');
    setSubmittingReview(true);
    try {
      await api(`/businesses/${businessId}/review`, {
        method: 'POST',
        body: JSON.stringify({ rating, comment: comment.trim() }),
      });
      toast.success('Review submitted');
      setComment('');
      setRating(5);
      await loadBusiness();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSubmittingReview(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  if (!business) return null;

  const images = business.images?.length ? business.images : [];

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid={`business-detail-${business.id}`}>
      {/* Header image */}
      <div className="relative aspect-[16/9] sm:aspect-[3/1] bg-[#FFE5DD] mb-6">
        {images[photoIdx] ? (
          <img src={images[photoIdx]} alt={business.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-16 h-16 text-[#C8553D]/30" />
          </div>
        )}
        <button
          onClick={() => navigate('/app/businesses')}
          data-testid="business-back-button"
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
        >
          <ArrowLeft className="w-5 h-5 text-[#2B2D42]" />
        </button>
        <button
          onClick={handleToggleSave}
          data-testid="business-detail-save-button"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
        >
          {saved ? <BookmarkCheck className="w-5 h-5 text-[#C8553D]" fill="currentColor" /> : <Bookmark className="w-5 h-5 text-[#6D6875]" />}
        </button>
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setPhotoIdx(i)}
                data-testid={`business-photo-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === photoIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="px-4 sm:px-6">
        {/* Header info */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#C8553D]" data-testid="business-detail-category">
              {business.category}
            </p>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2B2D42] mt-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="business-detail-name">
              {business.name}
            </h1>
          </div>
          {business.avg_rating > 0 && (
            <div className="text-right flex-shrink-0">
              <div className="flex items-center gap-1 text-xl font-bold text-[#2B2D42]" data-testid="business-detail-rating">
                <Star className="w-5 h-5 fill-[#E9C46A] text-[#E9C46A]" />
                {business.avg_rating.toFixed(1)}
              </div>
              <p className="text-xs text-[#6D6875]">{business.review_count || 0} reviews</p>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-4 text-sm text-[#6D6875] mb-5">
          {business.address && (
            <span className="flex items-center gap-1.5" data-testid="business-detail-address">
              <MapPin className="w-4 h-4" />
              {business.address}
            </span>
          )}
          {business.phone && (
            <a href={`tel:${business.phone}`} className="flex items-center gap-1.5 text-[#C8553D] hover:underline" data-testid="business-detail-phone">
              <Phone className="w-4 h-4" />
              {business.phone}
            </a>
          )}
          {business.website_url && (
            <a href={business.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#C8553D] hover:underline" data-testid="business-detail-website">
              <Globe className="w-4 h-4" />
              Website
            </a>
          )}
        </div>

        {/* Description */}
        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="business-detail-description">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2">About</h2>
          <p className="text-[#2B2D42] leading-relaxed">{business.description}</p>
        </section>

        {/* Reviews */}
        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="business-reviews-section">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">
            Reviews ({business.review_count || 0})
          </h2>

          {/* Add review */}
          <form onSubmit={handleSubmitReview} className="mb-5 pb-5 border-b border-[#F4F1DE]" data-testid="business-review-form">
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`business-rating-${n}`}
                  className="p-1"
                >
                  <Star className={`w-6 h-6 ${n <= rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-[#F4F1DE]'}`} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              data-testid="business-review-input"
              rows={2}
              className="w-full bg-[#FFFCF9] border border-[#F4F1DE] rounded-lg p-3 outline-none focus:border-[#E07A5F] text-sm text-[#2B2D42]"
              maxLength={500}
            />
            <button
              type="submit"
              disabled={submittingReview || !comment.trim()}
              data-testid="business-review-submit"
              className="mt-3 inline-flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold px-4 py-2 rounded-full text-sm"
            >
              {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit Review
            </button>
          </form>

          {/* Reviews list */}
          {(business.reviews || []).length === 0 ? (
            <p className="text-sm text-[#6D6875] italic" data-testid="business-reviews-empty">No reviews yet — be the first!</p>
          ) : (
            <ul className="space-y-4" data-testid="business-reviews-list">
              {business.reviews.map((rev, i) => (
                <li key={i} className="flex gap-3" data-testid={`business-review-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-[#FFE5DD] flex-shrink-0 overflow-hidden">
                    {rev.user?.photos?.[0] ? (
                      <img src={rev.user.photos[0]} alt={rev.user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-bold text-[#C8553D]">
                        {rev.user?.name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-sm text-[#2B2D42]">{rev.user?.name || 'User'}</p>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3 h-3 ${n <= rev.rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-[#F4F1DE]'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-[#2B2D42] mt-1 leading-relaxed">{rev.comment}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
